import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ExecutionDetailMetricsResponse } from '@/types/composer-dashboard';
import { Skeleton } from '@/components/ui/skeleton';
import { MetricCard } from './metric-card';
import { SectionHeading } from './section-heading';

interface ExecutionDetailSectionProps {
  metrics: ExecutionDetailMetricsResponse | undefined;
  isLoading?: boolean;
  error?: Error | null;
}

export function ExecutionDetailSection({
  metrics,
  isLoading = false,
  error,
}: ExecutionDetailSectionProps) {
  if (isLoading) {
    return (
      <div className='flex h-full flex-col gap-4'>
        <Skeleton className='h-8 w-64' />
        <div className='grid flex-1 grid-cols-1 gap-4'>
          {Array.from({ length: 2 }).map((_, idx) => (
            <Card key={idx} className='flex h-full min-h-0 flex-col'>
              <CardHeader className='pb-4'>
                <Skeleton className='h-6 w-32' />
              </CardHeader>
              <CardContent>
                <Skeleton className='h-10 w-20' />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || !metrics) {
    return (
      <div className='space-y-4'>
        <SectionHeading>Workflow Execution</SectionHeading>
        <div className='flex h-[120px] items-center justify-center text-muted-foreground'>
          <p className='text-sm'>
            {error?.message || 'No execution data available'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='flex h-full flex-col gap-4'>
      <SectionHeading>Workflow Execution</SectionHeading>
      <div className='grid flex-1 grid-cols-1 gap-4'>
        <MetricCard
          className='min-h-0'
          title={
            <span className='flex items-baseline gap-1.5'>
              Failed Runs
              <span className='text-xs font-normal text-muted-foreground'>
                Last 7 Days
              </span>
            </span>
          }
          metrics={[
            {
              label: 'Failure Rate',
              value: `${metrics.failed_run_percent_7d}%`,
            },
            {
              label: 'Failed Workflows',
              value: `${metrics.workflows_with_failed_run_7d} / ${metrics.total_workflows_with_runs}`,
            },
          ]}
        />
        <MetricCard
          className='min-h-0'
          title={
            <span className='flex items-baseline gap-1.5'>
              Run Time
              <span className='text-xs font-normal text-muted-foreground'>
                Completed Runs
              </span>
            </span>
          }
          metrics={[
            {
              label: 'Longest',
              value:
                metrics.longest_run_minutes !== null
                  ? `${metrics.longest_run_minutes} min`
                  : 'N/A',
            },
            {
              label: 'Shortest',
              value:
                metrics.shortest_run_minutes !== null
                  ? `${metrics.shortest_run_minutes} min`
                  : 'N/A',
            },
          ]}
        />
      </div>
    </div>
  );
}
